import { useEffect, useRef, useState } from "react";
import { Link, useParams } from "react-router-dom";
import { api, EvidenceRow } from "../lib/api";
import { sha256File } from "../lib/hash";
import { Logo } from "../components/Logo";
import { AigenticoBadge, AigenticoWordmark } from "../components/AigenticoBadge";
import { StatusPill } from "../components/StatusPill";

type Check =
  | { state: "idle" }
  | { state: "hashing"; progress: number }
  | { state: "match"; computed: string }
  | { state: "mismatch"; computed: string; expected: string }
  | { state: "error"; message: string };

export function Verify() {
  const { evidenceId } = useParams<{ evidenceId: string }>();
  const [row, setRow] = useState<EvidenceRow | null>(null);
  const [loadErr, setLoadErr] = useState<"" | "withdrawn" | "not_found" | "other">("");
  const [loadErrMsg, setLoadErrMsg] = useState("");
  const [check, setCheck] = useState<Check>({ state: "idle" });
  const fileRef = useRef<HTMLInputElement>(null);

  useEffect(() => {
    if (!evidenceId) return;
    api.getPublic(evidenceId)
      .then(setRow)
      .catch(e => {
        const msg = e instanceof Error ? e.message : String(e);
        // api.getPublic throws "HTTP 410" for withdrawn rows and "HTTP 404"
        // for evidence that never existed. We check text because api.ts does
        // not currently surface the raw status code.
        if (msg.includes("410") || msg.toLowerCase().includes("withdrawn") || msg.toLowerCase().includes("gone")) {
          setLoadErr("withdrawn");
        } else if (msg.includes("404") || msg.toLowerCase().includes("not found")) {
          setLoadErr("not_found");
        } else {
          setLoadErr("other");
        }
        setLoadErrMsg(msg);
      });
  }, [evidenceId]);

  async function onFile(f: File | null) {
    if (!f || !row?.sha256) return;
    setCheck({ state: "hashing", progress: 0 });
    try {
      const computed = await sha256File(f, (b, t) =>
        setCheck({ state: "hashing", progress: Math.round((b / t) * 100) }));
      if (computed.toLowerCase() === row.sha256!.toLowerCase()) {
        setCheck({ state: "match", computed });
      } else {
        setCheck({ state: "mismatch", computed, expected: row.sha256! });
      }
    } catch (err) {
      setCheck({ state: "error", message: err instanceof Error ? err.message : String(err) });
    }
  }

  return (
    <div className="min-h-screen flex flex-col">
      <header className="px-6 py-6 border-b border-border bg-white">
        <div className="max-w-4xl mx-auto flex items-center justify-between gap-4">
          <div className="flex items-center gap-4">
            <Link to="/"><Logo /></Link>
          </div>
          <div className="flex items-center gap-4">
            <div className="text-xs uppercase tracking-widest text-ink-faint">Public verifier</div>
            <a href="https://aigentico.ai" target="_blank" rel="noopener" title="An Aigentico product — visit Aigentico" className="shrink-0">
              <AigenticoBadge size={32} />
            </a>
          </div>
        </div>
      </header>

      <main className="flex-1 px-6 py-10">
        <div className="max-w-4xl mx-auto">
          {loadErr === "withdrawn" ? (
            <div className="border border-aigentico-red rounded-lg p-8 bg-white">
              <h2 className="text-aigentico-red mb-2">Evidence withdrawn</h2>
              <p className="text-ink-soft text-sm">
                The owner of this evidence has withdrawn the shareable link. The
                cryptographic proof (SHA-256 fingerprint, ledger row, and Bitcoin
                anchor) was recorded when the evidence was sealed and remains on
                the public Bitcoin blockchain — it just no longer resolves here.
              </p>
              <p className="text-ink-soft text-sm mt-3">
                If you are the person the owner shared this link with, please
                ask them for a fresh copy of the sealed file so you can verify
                the original SHA-256 fingerprint yourself.
              </p>
              <p className="hash mt-4">{evidenceId}</p>
            </div>
          ) : loadErr ? (
            <div className="border border-aigentico-red rounded-lg p-8 bg-white">
              <h2 className="text-aigentico-red mb-2">Not found</h2>
              <p className="text-ink-soft text-sm">
                No evidence exists for this identifier. Double-check the link
                you were sent — evidence IDs are long and one wrong character
                will not resolve.
              </p>
              <p className="hash mt-4">{evidenceId}</p>
              {loadErr === "other" && loadErrMsg && (
                <p className="text-ink-faint text-xs mt-3">{loadErrMsg}</p>
              )}
            </div>
          ) : !row ? (
            <div className="text-ink-faint text-sm uppercase tracking-widest py-24 text-center">Loading</div>
          ) : (
            <div>
              <div className="flex items-start justify-between gap-6 mb-8">
                <div>
                  <div className="text-xs uppercase tracking-widest text-aigentico-red mb-2 font-semibold">StampmatS evidence</div>
                  <h2 className="mb-2 break-all">{row.filename}</h2>
                  <div className="text-sm text-ink-soft">
                    Sealed {formatWhen(row.createdAt)} · {row.mimeType} · {formatBytes(row.size)}
                  </div>
                </div>
                <StatusPill row={row} />
              </div>

              <div className="grid gap-6">
                {row.label && (
                  <Panel title="Owner label">
                    <div className="text-ink text-sm break-words">{row.label}</div>
                    <p className="text-xs text-ink-faint mt-2">
                      This label was added by the evidence owner. It is not part of the sealed proof.
                    </p>
                  </Panel>
                )}

                <Panel title="Evidence ID">
                  <div className="hash">{row.evidenceId}</div>
                </Panel>

                {row.sha256 && (
                  <Panel title="Expected file hash (SHA-256)">
                    <div className="hash">{row.sha256}</div>
                  </Panel>
                )}

                {row.manifestHash && (
                  <Panel title="Manifest hash">
                    <div className="hash">{row.manifestHash}</div>
                    <p className="text-xs text-ink-faint mt-3">
                      This is the hash that was submitted to Bitcoin via OpenTimestamps.
                    </p>
                  </Panel>
                )}

                {typeof row.ledgerSeqNo === "number" && (
                  <Panel title="Position in the StampmatS evidence chain">
                    <div className="mono">#{row.ledgerSeqNo}</div>
                    {row.ledgerRowHash && <div className="hash mt-2">{row.ledgerRowHash}</div>}
                  </Panel>
                )}

                <Panel title="Bitcoin anchor">
                  {row.otsState === "anchored" ? (
                    <div>
                      <div className="text-verified text-sm uppercase tracking-widest mb-2 font-semibold">Anchored</div>
                      {typeof row.otsBitcoinBlockHeight === "number" && (
                        <p className="text-sm text-ink-soft">
                          Anchored to Bitcoin block <span className="mono">#{row.otsBitcoinBlockHeight}</span>
                          {typeof row.otsBitcoinBlockTime === "number" && (
                            <>
                              , mined on{" "}
                              <span className="whitespace-nowrap">
                                {new Date(row.otsBitcoinBlockTime * 1000).toUTCString().replace(" GMT", " UTC")}
                              </span>
                            </>
                          )}
                          .
                        </p>
                      )}
                      {row.otsBitcoinBlockHash && (
                        <p className="text-xs text-ink-faint mt-2 break-all">
                          Block hash: <span className="mono">{row.otsBitcoinBlockHash}</span>
                        </p>
                      )}
                      {typeof row.otsBitcoinBlockHeight === "number" && (
                        <p className="mt-4">
                          <a
                            href={`https://mempool.space/block/${row.otsBitcoinBlockHash || row.otsBitcoinBlockHeight}`}
                            target="_blank"
                            rel="noopener noreferrer"
                            className="text-verified text-sm font-semibold underline underline-offset-4 hover:no-underline"
                          >
                            View this block on the Bitcoin blockchain →
                          </a>
                        </p>
                      )}
                      <p className="text-xs text-ink-faint mt-4 max-w-prose">
                        The SHA-256 hash of this evidence's manifest is embedded, via OpenTimestamps, in a Merkle tree whose root is committed to this Bitcoin block. No party — not even StampmatS — can alter this record without rewriting Bitcoin's proof-of-work chain.
                      </p>
                    </div>
                  ) : (
                    <div>
                      <div className="text-pending text-sm uppercase tracking-widest mb-2 font-semibold">Awaiting Bitcoin</div>
                      <p className="text-sm text-ink-soft">
                        Submitted to {row.otsCalendarsAccepted ?? 0} OpenTimestamps calendar{(row.otsCalendarsAccepted ?? 0) === 1 ? "" : "s"}.
                        Bitcoin anchoring usually completes within 24 hours of upload. The timestamp is already independent of StampmatS and verifiable via OpenTimestamps.
                      </p>
                    </div>
                  )}
                </Panel>

                {row.sha256 && (
                  <Panel title="Verify the file yourself">
                    <p className="text-sm text-ink-soft mb-4 max-w-prose">
                      If someone gave you a copy of this file, drop it below. Your browser
                      will hash it locally and compare against the sealed hash. The file
                      never leaves your device.
                    </p>
                    <input
                      ref={fileRef} type="file" className="hidden"
                      onChange={e => onFile(e.target.files?.[0] ?? null)}
                    />
                    <button
                      onClick={() => fileRef.current?.click()}
                      className="btn-secondary"
                      disabled={check.state === "hashing"}
                    >
                      {check.state === "hashing" ? "Hashing…" : "Choose file to verify"}
                    </button>

                    {check.state === "hashing" && (
                      <div className="mt-4">
                        <div className="h-1.5 bg-border rounded-full overflow-hidden">
                          <div className="h-full bg-aigentico-navy transition-all" style={{ width: `${check.progress}%` }} />
                        </div>
                      </div>
                    )}
                    {check.state === "match" && (
                      <div className="mt-4 border border-verified bg-white p-4 rounded-md">
                        <div className="text-verified text-sm uppercase tracking-widest mb-2 font-semibold">Match</div>
                        <p className="text-sm text-ink-soft">The file you provided matches the sealed hash exactly.</p>
                        <div className="hash mt-3">{check.computed}</div>
                      </div>
                    )}
                    {check.state === "mismatch" && (
                      <div className="mt-4 border border-aigentico-red bg-white p-4 rounded-md">
                        <div className="text-aigentico-red text-sm uppercase tracking-widest mb-2 font-semibold">Mismatch</div>
                        <p className="text-sm text-ink-soft">The file you provided is not the file that was sealed.</p>
                        <div className="mt-3">
                          <div className="text-xs text-ink-faint mb-1 uppercase tracking-widest">Expected</div>
                          <div className="hash">{check.expected}</div>
                          <div className="text-xs text-ink-faint mt-2 mb-1 uppercase tracking-widest">Computed</div>
                          <div className="hash">{check.computed}</div>
                        </div>
                      </div>
                    )}
                    {check.state === "error" && (
                      <div className="mt-4 text-aigentico-red text-sm">{check.message}</div>
                    )}
                  </Panel>
                )}
              </div>
            </div>
          )}
        </div>
      </main>

      <footer className="px-6 py-6 border-t border-border bg-white">
        <div className="max-w-4xl mx-auto flex flex-wrap items-center justify-between gap-4 text-xs text-ink-faint">
          <Link to="/" className="hover:text-ink">StampmatS — Digital evidence that survives.</Link>
          <AigenticoWordmark />
        </div>
      </footer>
    </div>
  );
}

function Panel({ title, children }: { title: string; children: React.ReactNode }) {
  return (
    <section className="panel">
      <div className="text-xs uppercase tracking-widest text-aigentico-navy mb-3 font-semibold">{title}</div>
      {children}
    </section>
  );
}

function formatBytes(n: number): string {
  if (n < 1024) return `${n} B`;
  if (n < 1024 * 1024) return `${(n / 1024).toFixed(1)} KB`;
  return `${(n / 1024 / 1024).toFixed(2)} MB`;
}

function formatWhen(iso: string): string {
  const d = new Date(iso);
  return d.toLocaleString(undefined, { dateStyle: "medium", timeStyle: "short" });
}
